import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaArrowDown } from 'react-icons/fa';

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [chatHeight, setChatHeight] = useState(500);
  const [chatWidth, setChatWidth] = useState(350);
  const [isResizing, setIsResizing] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm here to help you learn more about Digital Biz Tech's services. How can I assist you today?",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(() => 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9));
  const [showScrollButton, setShowScrollButton] = useState(false);
  
  const chatRef = useRef(null);
  const resizeRef = useRef(null);
  const chatBodyRef = useRef(null);
  const inputRef = useRef(null);

  const WEBHOOK_URL = 'https://n8n-jabpzjqf.us-west-1.clawcloudrun.com/webhook/d536879e-1fa5-45f6-b106-25f580ea92f4/chat';
  

  const quickReplies = [
    "Tell me about Salesforce services",
    "AI Automation solutions", 
    "SAP S/4 HANA migration",
    "Talent acquisition services",
    "Contact information"
  ];

  // Auto-scroll to bottom function
  const scrollToBottom = () => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
      setShowScrollButton(false);
    }
  };

  // Check if user has scrolled up
  const handleScroll = () => {
    if (chatBodyRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = chatBodyRef.current;
      const scrolledFromBottom = scrollHeight - (scrollTop + clientHeight);
      
      // Only show scroll button if user has scrolled up more than 100px from bottom
      const scrollThreshold = 100;
      setShowScrollButton(scrolledFromBottom > scrollThreshold);
    }
  };

  // Auto-scroll when new messages are added
  useEffect(() => {
    if (!showScrollButton) {
      scrollToBottom();
    }
  }, [messages, isTyping]);

  // Auto-scroll when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 100);
    }
  }, [isOpen]);

  // Handle mouse events for diagonal resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const chatWindow = chatRef.current;
      if (!chatWindow) return;
      
      const rect = chatWindow.getBoundingClientRect();
      
      // Calculate new dimensions based on mouse position relative to top-left corner
      const newHeight = rect.bottom - e.clientY;
      const newWidth = e.clientX - rect.left;
      
      // Set constraints
      const minHeight = 300;
      const maxHeight = window.innerHeight - 100;
      const minWidth = 280;
      const maxWidth = Math.min(window.innerWidth - 50, 600);
      
      // Update height if within constraints
      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setChatHeight(newHeight);
      }
      
      // Update width if within constraints
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setChatWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = 'default';
      document.body.style.userSelect = 'auto';
    };

    if (isResizing) {
      document.body.style.cursor = 'nw-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Send message to n8n webhook
  const sendToWebhook = async (message) => {
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: message,
          sessionId: sessionId,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.response || data.message || "I'm here to help! Please let me know what you'd like to know about Digital Biz Tech's services.";
    } catch (error) {
      console.error('Error sending message to webhook:', error);
      return "I'm sorry, I'm having trouble connecting right now. Please try again or contact us directly at (614) 726-1818.";
    }
  };

  const sendMessage = async (text = inputMessage) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: text,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Keep focus on input after sending message
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);

    try {
      // Send to n8n webhook and get response
      const botResponse = await sendToWebhook(text);
      
      const botMessage = {
        id: Date.now() + 1,
        text: botResponse,
        isBot: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "I'm sorry, I'm having trouble responding right now. Please try again or contact us directly at (614) 726-1818.",
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleQuickReply = (reply) => {
    sendMessage(reply);
  };

  const chatbotStyles = {
    chatbotContainer: {
      position: 'fixed',
      bottom: '20px',
      right: '20px',
      zIndex: 1000,
      fontFamily: 'Arial, sans-serif'
    },
    chatButton: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      backgroundColor: '#ed8139',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      transition: 'all 0.3s ease'
    },
    chatWindow: {
      position: 'absolute',
      bottom: '70px',
      right: '0',
      width: `${chatWidth}px`,
      height: `${chatHeight}px`,
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: isResizing ? 'none' : 'width 0.2s ease, height 0.2s ease'
    },
    resizeHandle: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '20px',
      height: '20px',
      cursor: 'nw-resize',
      backgroundColor: 'transparent',
      zIndex: 10,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start'
    },
    resizeIndicator: {
      position: 'absolute',
      top: '6px',
      left: '6px',
      width: '10px',
      height: '10px',
      display: 'grid',
      gridTemplate: '1fr 1fr / 1fr 1fr',
      gap: '2px'
    },
    resizeDot: {
      width: '3px',
      height: '3px',
      backgroundColor: '#ced4da',
      borderRadius: '50%'
    },
    chatHeader: {
      backgroundColor: '#2c3e50',
      color: 'white',
      padding: '15px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingTop: '25px',
      paddingLeft: '25px'
    },
    chatBody: {
      flex: 1,
      padding: '15px',
      overflowY: 'auto',
      backgroundColor: '#f8f9fa',
      position: 'relative',
      scrollBehavior: 'smooth'
    },
    scrollToBottomButton: {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      backgroundColor: '#ed8139',
      color: 'white',
      border: 'none',
      cursor: 'pointer',
      display: showScrollButton ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      transition: 'all 0.3s ease',
      zIndex: 20
    },
    message: {
      marginBottom: '12px',
      display: 'flex',
      flexDirection: 'column'
    },
    userMessage: {
      alignSelf: 'flex-end',
      backgroundColor: '#ed8139',
      color: 'white',
      padding: '10px 15px',
      borderRadius: '18px 18px 5px 18px',
      maxWidth: '80%',
      wordWrap: 'break-word'
    },
    botMessage: {
      alignSelf: 'flex-start',
      backgroundColor: 'white',
      color: '#333',
      padding: '10px 15px',
      borderRadius: '18px 18px 18px 5px',
      maxWidth: '80%',
      border: '1px solid #e1e5e9',
      wordWrap: 'break-word',
      whiteSpace: 'pre-line'
    },
    quickReplies: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      marginTop: '10px'
    },
    quickReply: {
      backgroundColor: '#e9ecef',
      border: '1px solid #ced4da',
      borderRadius: '15px',
      padding: '8px 12px',
      fontSize: '13px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'left'
    },
    inputContainer: {
      padding: '15px',
      borderTop: '1px solid #e1e5e9',
      display: 'flex',
      gap: '10px'
    },
    input: {
      flex: 1,
      padding: '10px 15px',
      border: '1px solid #ced4da',
      borderRadius: '20px',
      outline: 'none',
      fontSize: '14px'
    },
    sendButton: {
      backgroundColor: '#ed8139',
      color: 'white',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    typing: {
      alignSelf: 'flex-start',
      backgroundColor: 'white',
      padding: '10px 15px',
      borderRadius: '18px 18px 18px 5px',
      border: '1px solid #e1e5e9',
      fontStyle: 'italic',
      color: '#6c757d'
    }
  };

  return (
    <div style={chatbotStyles.chatbotContainer}>
      {isOpen && (
        <div style={chatbotStyles.chatWindow} ref={chatRef}>
          {/* Diagonal Resize Handle */}
          <div
            style={chatbotStyles.resizeHandle}
            onMouseDown={handleResizeStart}
            ref={resizeRef}
            title="Drag to resize chat window"
          >
            <div style={chatbotStyles.resizeIndicator}>
              <div style={chatbotStyles.resizeDot}></div>
              <div style={chatbotStyles.resizeDot}></div>
              <div style={chatbotStyles.resizeDot}></div>
              <div style={chatbotStyles.resizeDot}></div>
            </div>
          </div>

          <div style={chatbotStyles.chatHeader}>
            <div>
              <div style={{ fontWeight: 'bold' }}>DBT Assistant</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>
                {isTyping ? 'Typing...' : 'Online'}
              </div>
            </div>
            
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              <FaTimes />
            </button>
          </div>

          <div 
            style={chatbotStyles.chatBody} 
            ref={chatBodyRef}
            onScroll={handleScroll}
          >
            {messages.map((message) => (
              <div key={message.id} style={chatbotStyles.message}>
                <div
                  style={
                    message.isBot
                      ? chatbotStyles.botMessage
                      : chatbotStyles.userMessage
                  }
                >
                  {message.text}
                </div>
              </div>
            ))}

            {isTyping && (
              <div style={chatbotStyles.typing}>
                DBT Assistant is typing...
              </div>
            )}

            {messages.length === 1 && (
              <div style={chatbotStyles.quickReplies}>
                <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>
                  Quick questions:
                </div>
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    style={chatbotStyles.quickReply}
                    onClick={() => handleQuickReply(reply)}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#dee2e6';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = '#e9ecef';
                    }}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            {/* Scroll to Bottom Button - Centered */}
            <button
              style={chatbotStyles.scrollToBottomButton}
              onClick={scrollToBottom}
              title="Scroll to bottom"
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#d16d2a';
                e.target.style.transform = 'translateX(-50%) scale(1.1)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#ed8139';
                e.target.style.transform = 'translateX(-50%) scale(1)';
              }}
            >
              <FaArrowDown />
            </button>
          </div>

          <div style={chatbotStyles.inputContainer}>
            <input
              ref={inputRef}
              type="text"
              placeholder="Type your message..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              style={chatbotStyles.input}
              disabled={isTyping}
            />
            <button
              onClick={() => sendMessage()}
              style={{
                ...chatbotStyles.sendButton,
                opacity: isTyping ? 0.6 : 1,
                cursor: isTyping ? 'not-allowed' : 'pointer'
              }}
              disabled={isTyping}
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      )}

      <button
        style={chatbotStyles.chatButton}
        onClick={() => setIsOpen(!isOpen)}
        onMouseOver={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.backgroundColor = '#d16d2a';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.backgroundColor = '#ed8139';
        }}
      >
        {isOpen ? <FaTimes /> : <FaComments />}
      </button>
    </div>
  );
}
